'use client';
import { useTimeSettings } from '@mnd-frontend/contexts';
import { monthNameShort, shortDateString } from '@mnd-frontend/dates';
import { useBrowserEffect } from '@mnd-frontend/hooks';
import { ticks } from 'd3-array';
import { axisBottom } from 'd3-axis';
import { select } from 'd3-selection';
import { utcHour } from 'd3-time';
import { memo, type RefObject, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { DataPoint, Scale, ScaleBand } from '../../../../types';
import { useEventContext, useSvgContext } from '../../../context';
import { useScaleBandX } from '../../../hooks/d3';
import AxisStyle from './AxisStyle';

const getAxisWidth = (scale: Scale) => scale.range().reduce((x0, x1) => x1 - x0);

const getLastTickWidth = (nodes?: HTMLCollection) =>
  (nodes && nodes.length > 1 && nodes[nodes.length - 1].getBoundingClientRect().width) || 0;

const getFirstTickWidth = (nodes?: HTMLCollection) =>
  (nodes && nodes.length > 1 && nodes[1].getBoundingClientRect().width) || 0;

const getMinTickInterval = (
  axisWidth: number,
  tickWidth: number,
  totalStepCount: number,
  gap: number,
) => {
  // step: how many data points fit between the 2 ticks |····|step|····|
  const maxFittingStep = Math.max(1, axisWidth / (tickWidth || 1) / (1 + gap));
  // ceil to the smallest integer interval (data point diff between 2 adjacent ticks)
  // which makes ticks have at least the (relative) gap between each other
  return Math.ceil(totalStepCount / maxFittingStep);
};

const Bottom = memo(
  ({
    scale,
    y,
    data,
    tickGap = 0.5,
    tickPadding = 18,
  }: {
    scale: ScaleBand;
    y: number;
    data: DataPoint[];
    tickGap?: number;
    tickPadding?: number;
  }) => {
    const ref = useRef<SVGGElement>(null);
    const { updatePaddingIfLarger } = useEventContext();
    const [maxTickWidth, setMaxTickWidth] = useState(1);
    const [minTickInterval, setMinTickInterval] = useState(1);

    const totalSteps = data.length - 1;

    // increase interval as much as possible without decreasing the amount of ticks
    // maxTicks = floor((data.length - 1) / minTickInterval) + 1
    // interval = floor((data.length - 1) / (maxTicks - 1))
    const tickInterval = Math.floor(totalSteps / Math.floor(totalSteps / minTickInterval));

    const tickOff = totalSteps % tickInterval;
    const domain = scale.domain();
    const allTicks = ticks(domain[0], domain[domain.length - 1], totalSteps);

    const visibleTicks = allTicks.filter((t, i) => data[t] && i % tickInterval === tickOff);

    useEffect(() => {
      const axis = axisBottom(scale)
        .tickPadding(tickPadding)
        .tickSize(0)
        .tickValues(visibleTicks)
        .tickFormat((d: any) => data[d].name);
      select(ref.current).call(axis as any);

      // measure rendered axis
      const axisWidth = getAxisWidth(scale);
      const firstTickWidth = getFirstTickWidth(ref.current?.children);
      const lastTickWidth = getLastTickWidth(ref.current?.children);

      // estimate the max tick width by measuring the first and the last tick
      // we need to remember max tick width to prevent it from shrinking
      // which could cause an infinite update loop
      const tickWidth = Math.max(firstTickWidth, lastTickWidth, maxTickWidth);
      if (tickWidth > maxTickWidth) {
        setMaxTickWidth(tickWidth);
      }

      // calculate and update the minimum tick interval
      setMinTickInterval(getMinTickInterval(axisWidth, tickWidth, data.length - 1, tickGap));

      // there is already some space available in scaleBand axes

      const available =
        scale?.bandwidth?.() / scale?.paddingOuter?.() - scale?.bandwidth?.() / 2 || 0;

      // update paddings according to the max tick width
      updatePaddingIfLarger({
        bottom: (ref.current as any).getBoundingClientRect().height,
        right: Math.round(Math.max(lastTickWidth / 2 - available, 0)),
        left: Math.round(Math.max(firstTickWidth / 2 - available, 0)),
      });
    }, [data, scale, visibleTicks, updatePaddingIfLarger, tickGap, tickPadding, maxTickWidth]);

    return <AxisStyle ref={ref} transform={`translate(0, ${y})`} />;
  },
);

const useMeasure = (
  ref: RefObject<SVGGElement>,
  text: string,
  callback: ({ width, height }: { width: number; height: number }) => void,
) => {
  const [size, setSize] = useState({ width: 1, height: 1 });
  useBrowserEffect(() => {
    const selection = select(ref.current).append('text').html(text);
    const { width, height } = (selection.node() as any).getBoundingClientRect();
    selection.remove();
    setSize({ width, height });
    callback && callback({ width, height });
  }, [ref, text, callback]);
  return size;
};

export const Time = memo(
  ({
    scale,
    y,
    tickGap = 0.5,
    tickPadding = 18,
  }: {
    scale: any;
    y: number;
    tickGap?: number;
    tickPadding?: number;
  }) => {
    const ref = useRef<SVGGElement>(null);
    const { updatePaddingIfLarger } = useEventContext();
    const { interval } = scale;
    const {
      material: { locale, timezone },
    } = useTimeSettings();

    const axisWidth = getAxisWidth(scale);
    const totalDays = interval?.count(...scale.domain()) || 1;

    const applyMeasurements = useCallback(
      (size: { width: number; height: number }) => {
        updatePaddingIfLarger({
          bottom: Math.ceil(tickPadding + size.height),
          right: Math.round(size.width / 2),
          left: Math.round(size.width / 2),
        });
      },
      [tickPadding, updatePaddingIfLarger],
    );

    const longDate = '00. MMMM 0000';
    const { width: tickWidth } = useMeasure(ref, longDate, applyMeasurements);

    const minInterval = getMinTickInterval(axisWidth, tickWidth, totalDays, tickGap);

    useEffect(() => {
      const [startDate, endDate] = scale.domain();
      const preserveFirstTick = 0;
      const d3Interval = interval.filter(
        (d: any) => interval.count(startDate, d) % minInterval === preserveFirstTick,
      );

      const allTicks = scale
        .ticks(d3Interval)
        .map((d: any) => utcHour.offset(d, 12))
        .filter((d: any) => d < endDate);

      const axis = axisBottom(scale)
        .tickPadding(tickPadding)
        .tickSize(0)
        .tickValues(allTicks)
        .tickFormat((d: any) => shortDateString(d, timezone, locale));
      select(ref.current).call(axis as any);
    }, [scale, interval, tickPadding, minInterval, locale, timezone]);

    return <AxisStyle ref={ref as any} transform={`translate(0, ${y})`} />;
  },
);

export default Bottom;

export const BottomStrict = ({
  scale,
  y,
  data,
  extraPadding = 0,
}: {
  scale: ScaleBand;
  y: number;
  extraPadding?: number;
  data: { name: string }[];
}) => {
  const ref = useRef<SVGGElement>(null);
  const { updatePaddingIfLarger } = useEventContext();
  const axis = useMemo(() => {
    const totalSteps = data.length - 1;
    const domain = scale.domain();
    const allTicks = ticks(domain[0], domain[domain.length - 1], totalSteps);
    return axisBottom(scale)
      .tickPadding(16)
      .tickSize(0)
      .tickValues(allTicks)
      .tickFormat(d => {
        return data[d].name;
      });
  }, [scale, data]);

  const { right, left, bottom } = useSvgContext();

  useEffect(() => {
    if (ref.current) {
      select(ref.current).call(axis as any);

      const firstTickWidth = getFirstTickWidth(ref.current?.children);
      const lastTickWidth = getLastTickWidth(ref.current?.children);

      const available =
        scale?.bandwidth?.() / scale?.paddingOuter?.() - scale?.bandwidth?.() / 2 || 0;

      const newRight = Math.round(Math.max(lastTickWidth / 2 - available, 0));
      const newLeft = Math.round(Math.max(firstTickWidth / 2 - available, 0));
      const newBottom = (ref.current as any).getBoundingClientRect().height + extraPadding;
      const updateObj: { left?: number; bottom?: number; right?: number } = {};
      if (newRight > right) {
        updateObj['right'] = newRight;
      }
      if (newLeft > left) {
        updateObj['left'] = newLeft;
      }
      if (newBottom > bottom) {
        updateObj['bottom'] = newBottom;
      }

      // update paddings according to the max tick width
      updatePaddingIfLarger(updateObj);
    }
  }, [axis, scale, updatePaddingIfLarger, extraPadding, right, left, bottom]);

  return <AxisStyle ref={ref} transform={`translate(0, ${y})`} $noStroke />;
};

export const BottomMonths = ({
  data,
  y,
  extraPadding,
}: {
  data: { month?: number }[] /* 1 based. Jan=1 etc */;
  y: number;
  extraPadding: number;
}) => {
  const {
    material: { locale },
  } = useTimeSettings();
  /* remove direct duplicates. [0,0,1,0] becomes [0,1,0] */
  const reducedData = data.reduce((acc: { month?: number }[], item) => {
    if (acc.at(-1)?.month === item.month) return acc;
    return [...acc, item];
  }, []);

  const monthData = reducedData.map(item => ({
    name: monthNameShort(new Date(2000, (item?.month || 1) - 1, 1), locale),
  }));
  const scaleX = useScaleBandX(monthData, 0, 0.3);

  return (
    <BottomStrict
      data={monthData}
      scale={scaleX}
      y={y + extraPadding}
      extraPadding={extraPadding}
    />
  );
};
